<template>
    <BaseConfirm
        style="width: 780px"
        :title="'포인트 지급 요청'"
        :isOpen="isOpen"
        :confirm-text="'등록'"
        :ignore-backdrop-click="true"
        @accept="submitHandler"
        @cancel="closeHandler"
    >
        <form ref="pointProvideRequestFormDOM">
            <div class="form-input-group">
                <div class="title">
                    <p class="required">지급사유</p>
                    <p class="caption">최대 50자까지 입력 가능</p>
                </div>
                <div class="contents">
                    <BaseInput
                        :name="'description'"
                        :placeholder="'예시: 2023년 복지 포인트 (최대 50자)'"
                        :hasError="submitCount > 0 && !!errors.description"
                        v-model="description"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">지급유형</p>
                </div>
                <div class="contents">
                    <BaseSelect
                        :Name="'policyId'"
                        :option-list="bizPointPolicies"
                        v-model="policyId"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">유효기간</p>
                    <p class="caption">현재날짜 이후 날짜로만 입력 가능</p>
                </div>
                <div class="contents">
                    <DatePicker
                        style="width: 332px"
                        :name="'expireDate'"
                        :minDate="'today'"
                        :placeholder="'유효기간을 선택해주세요.'"
                        :hasError="submitCount > 0 && !!errors.expireDate"
                        v-model="expireDate"
                        @destroyDatePicker="destroyDatePicker"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title" style="padding-top: 0">
                    <p class="required">지급형태</p>
                    <div class="caption">
                        <p>
                            유효기간보다 이전 날짜 및<br />
                            분(minutes)은 10분 단위로만 입력 가능
                        </p>
                        <p style="margin-top: 3px">
                            ※ 예시：14:10, 09:00, 20:50
                        </p>
                    </div>
                </div>
                <div class="contents">
                    <BaseRadio
                        :name="'type'"
                        v-model="type"
                        :value="'IMMEDIATELY'"
                        :label="'즉시지급'"
                        :is-display-block="true"
                    />
                    <div class="type-reserve-row">
                        <BaseRadio
                            :name="'type'"
                            :value="ServiceTypeE.RESERVE"
                            :label="'예약지급'"
                            v-model="type"
                        />

                        <!-- NOTE: 예약지급일시는 현재시간으로부터 30분 이후부터의 시간만 저장 가능 -->
                        <DatePicker
                            style="width: 230px"
                            :name="'reservedAt'"
                            :placeholder="'예약 일시를 선택해주세요.'"
                            :enableTime="true"
                            :minDate="
                                DateTime.now()
                                    .plus({minutes: 30})
                                    .toFormat('yyyy-MM-dd HH:mm')
                            "
                            :minuteIncrement="10"
                            :disabled="type !== ServiceTypeE.RESERVE"
                            :hasError="submitCount > 0 && !!errors.reservedAt"
                            v-model="reservedAt"
                            @updateDate="onChangeReversedAt"
                            @destroyDatePicker="destroyDatePicker"
                        />
                    </div>

                    <div
                        v-if="submitCount > 0 && !!errors.type"
                        class="error-message"
                    >
                        {{ errors.type }}
                    </div>
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">미가입자 지급대기기간</p>
                    <p class="caption">유효기간보다 이전 날짜로만 입력 가능</p>
                </div>
                <div class="contents">
                    <DatePicker
                        style="width: 332px"
                        :name="'limitedAt'"
                        :minDate="'today'"
                        :maxDate="expireDate"
                        :placeholder="'미가입자 지급대기기간을 선택해주세요.'"
                        :hasError="submitCount > 0 && !!errors.limitedAt"
                        v-model="limitedAt"
                        @destroyDatePicker="destroyDatePicker"
                    />
                </div>
            </div>

            <div class="form-input-group">
                <div class="title">
                    <p class="required">지급대상</p>
                    <p class="caption">
                        첨부 파일명은 확장자(.xlsx, .xls) 포함<br />
                        최대 50자까지 가능
                    </p>
                </div>
                <div class="contents">
                    <div class="file-input-row">
                        <input
                            ref="fileInputDOM"
                            type="file"
                            name="excelFile"
                            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            @change="onFileChange"
                        />

                        <div
                            :class="[
                                'file-path-box',
                                {
                                    'file-path-box--empty': !isFileUploaded,
                                    'file-path-box--invalid':
                                        submitCount > 0 && !!errors.excelFile,
                                },
                            ]"
                            @click="uploadFile"
                        >
                            {{
                                excelFile ||
                                '첨부된 파일이 없습니다. (첨부 파일명은 확장자 포함 최대 50자)'
                            }}
                        </div>

                        <div class="flex justify-between">
                            <div class="error-message">
                                <p v-if="submitCount > 0 && errors.excelFile">
                                    {{ errors.excelFile }}
                                </p>
                            </div>

                            <NButton
                                class="upload-btn"
                                icon-placement="right"
                                type="primary"
                                size="large"
                                strong
                                @click="uploadFile"
                            >
                                <template #icon>
                                    <img
                                        src="@/assets/image/icn_upload.png"
                                        alt="지급대상 파일 첨부 버튼 아이콘"
                                    />
                                </template>
                                지급대상 파일 첨부
                            </NButton>
                        </div>
                    </div>

                    <div class="btn-row">
                        <NButton
                            type="success"
                            size="large"
                            strong
                            @click="downloadExcelSampleFile"
                        >
                            신청양식 파일 다운로드
                        </NButton>
                    </div>

                    <div class="notice">
                        <p>유의사항</p>
                        <ul class="list-dots">
                            <li>
                                반드시
                                <span class="font-bold"
                                    >지급 신청양식 파일을 사용하여 지급 요청을
                                    진행</span
                                >해 주셔야 합니다. 상단 초록색 「신청양식 파일
                                다운로드」 버튼을 클릭하시면, 신청양식 파일을
                                받을 수 있습니다.
                            </li>
                            <li>
                                1회 업로드 시 최대 30,000명까지 지급 요청이
                                가능합니다.
                            </li>
                            <li>
                                파일 내 동일 회원 2회 이상 입력 시 중복 지급이
                                가능합니다.
                            </li>
                            <li>
                                예약 지급 적용 시 지정한 날짜에 실 지급
                                처리됩니다.
                            </li>
                            <li>
                                잘못 기입한 대상에게 포인트 지급된 경우 당사는
                                책임지지 않습니다.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </form>
    </BaseConfirm>
</template>

<script setup lang="ts">
import { getBizPointTargetList } from '@/api/points/biz-point-api';
import {fetchPointProvideRequest as fetchPointProvideRequestApi} from '@/api/points/point-request-history-api';
import BaseInput from '@/components/common/input/BaseInput.vue';
import BaseRadio from '@/components/common/input/BaseRadio.vue';
import BaseSelect from '@/components/common/input/BaseSelect.vue';
import DatePicker from '@/components/common/input/DatePicker.vue';
import BaseConfirm from '@/components/common/popup/BaseConfirm.vue';
import {useFieldModelCustom} from '@/hooks/vee-validate/useFieldModelCustom';
import {ServiceTypeE} from '@/types/points/point-keyword-types';
import {PointProvideRequestFormStateT} from '@/types/points/point-request-history-types';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {DateTime} from 'luxon';
import {Instance} from 'modules/flatpickr/dist/types/instance';
import {NButton} from 'naive-ui';
import {useForm} from 'vee-validate';
import {computed, onMounted, ref} from 'vue';
import {object, string} from 'yup';

// props, emits, libs
interface PropsI {
    isOpen: boolean;
}

withDefaults(defineProps<PropsI>(), {
    isOpen: false,
});

const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'refresh'): void;
    (e: 'destroyDatePicker'): void;
}>();

const {
    values: formState,
    setFieldValue,
    resetForm,
    submitCount,
    errors,
    handleSubmit,
} = useForm({
    initialValues: {
        description: '',
        expireDate: '',
        type: '',
        reservedAt: '',
        limitedAt: '',
        excelFile: '',
        policyId: -1,
    } as PointProvideRequestFormStateT,
    validationSchema: object({
        description: string()
            .required('지급사유를 입력해주세요.')
            .matches(
                /* eslint-disable no-useless-escape */
                /^(?!.*[@|#|%|&|*|\/|\\|~]).*(?=[a-zA-Z0-9ㄱ-힣]).*$/,
                `특수문자 @, #, %, &, *, /, ＼, ~는 사용할 수 없습니다.`,
            )
            .max(
                50,
                (description) =>
                    `지급사유가 너무 깁니다. 지급사유는 50자 이하까지 입력 가능합니다.\n※ 현재 지급사유 길이: ${description.value.length}자`,
            ),
        expireDate: string().required('유효기간을 선택해주세요.'),
        type: string().required('지급형태를 선택해주세요.'),
        reservedAt: string().when(() => {
            switch (formState.type) {
                case 'RESERVE':
                    return string().required('예약 일시를 선택해주세요.');
                default:
                    return string().notRequired();
            }
        }),
        limitedAt: string().required('미가입자 지급대기기간을 선택해주세요.'),
        excelFile: string()
            .required('xlsx, xls 형식의 지급대상 Excel 파일을 업로드해주세요.')
            .max(
                50,
                (excelFile) =>
                    `파일명이 너무 깁니다. 파일명은 확장자(.xlsx, .xls) 포함 50자까지 가능합니다.\n※ 현재 파일명 길이: ${excelFile.value.length}자`,
            ),
    }),
});

// Form state 참조 & 업데이트용
const {description, expireDate, type, reservedAt, limitedAt, excelFile, policyId} =
    useFieldModelCustom<{
        description: string;
        expireDate: string;
        type: string;
        reservedAt: string;
        limitedAt: string;
        excelFile: string;
        policyId: number;
    }>([
        'description',
        'expireDate',
        'type',
        'reservedAt',
        'limitedAt',
        'excelFile',
        'policyId'
    ]);

// ref, reactive
const fileInputDOM = ref<HTMLInputElement>();
const pointProvideRequestFormDOM = ref<HTMLFormElement>();

const bizPointPolicies = ref<{text: string, value: number}[]>([]);

// computed
const isFileUploaded = computed(() => formState.excelFile);

// methods
const closeHandler = () => {
    resetForm();
    destroyDatePicker();
    emits('close');
};

// 포인트 지급유형
const getBizPointPolicies = async () => {
    try {
        const response = await getBizPointTargetList({
            type: 'SAVE',
        });
        bizPointPolicies.value = response.map(o => {
            return {
                text: o.title,
                value: o.policyId,
            }
        });

        if (bizPointPolicies.value.length > 0) {
            setFieldValue('policyId', bizPointPolicies.value[0].value);
        }
    } catch (e) {
        console.log(e);
    }
}

const submitHandler = handleSubmit(async () => {
    if (!confirm(`이대로 포인트 지급 요청을 진행하시겠습니까?`)) return;

    try {
        // FIXME: postForm으로 수정
        const formData = new FormData(pointProvideRequestFormDOM.value);
        await fetchPointProvideRequestApi(formData);

        alert('포인트 지급 요청이 완료되었습니다.');
        closeHandler();

        // NOTE: 요청 완료후 내역 리스트 0페이지로 refresh
        emits('refresh');
    } catch (err) {
        const axiosError = err as AxiosError;

        console.error(axiosError);

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                header: axiosError.config.headers,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-history/popup/PointProvideRequestPopup.vue',
            },
        });

        window.customAlert(axiosError.message);
    }
});

/**
 * @description 샘플 파일 다운로드
 */
const downloadExcelSampleFile = () => {
    location.href = `${
        import.meta.env.VITE_APP_BASE_URL
    }/files/sample_point_request.xlsx`;
};

/**
 * @description display none 처리되어있는 input click 트리거하여, 파일 업로드창 오픈
 */
const uploadFile = () => {
    fileInputDOM.value?.click();
};

/**
 * @description input file 업로드 시, formState excelFile에 파일 path 저장
 */
const onFileChange = (e: Event) => {
    if (!fileInputDOM.value!.value) return;

    const isExcelFile = ['xlsx', 'xls'].some((excelFile) => {
        return fileInputDOM.value!.value.includes(excelFile);
    });

    if (!isExcelFile) {
        alert(
            'xlsx, xls 형식의 Excel 파일만 업로드할 수 있습니다. 파일 형식을 다시 한 번 확인해 주세요.',
        );
        return;
    }

    const target = e.target as HTMLInputElement;

    // HACK: input value에 한글이 있을때 자소분리되어버림. 자소분리된 문자를 정규화하여 결합 + input에 C:\fakepath\ string 제거하여 표시
    // See: https://egg-programmer.tistory.com/293
    const fileName = target.value.normalize().replace(/C:\\fakepath\\/i, '');

    setFieldValue('excelFile', fileName);
};

/**
 * @description 예약지급일시 분(minutes) 값이 10분 단위가 아닐때, 반올림 처리후 10분 단위로 분(minutes)값 출력
 * 예약지급 배치가 10분 단위로 돌고 있기 때문에, 시각을 무조건 10분 단위로만 선택 가능하도록 대응
 * @param param
 */
const onChangeReversedAt = ({
    selectedDates,
    dateString,
    instance,
}: {
    selectedDates: Date[];
    dateString: string;
    instance: Instance;
}) => {
    setTimeout(() => {
        const latestSelectedDate = instance.latestSelectedDateObj as Date;
        const mins = latestSelectedDate.getMinutes();

        // NOTE: 입력받은 분(minutes)값을 10으로 나눴을 때, 나머지가 있으면 반올림 처리후 10분 단위로 분(minutes)값 출력
        if (mins % 10) {
            latestSelectedDate.setMinutes(
                10 * Math.round(latestSelectedDate.getMinutes() / 10),
            );
        }

        instance.setDate(latestSelectedDate, false); // DatePicker 값 업데이트
        setFieldValue(
            'reservedAt',
            DateTime.fromJSDate(latestSelectedDate).toFormat(
                'yyyy-MM-dd HH:mm',
            ),
        ); // vee-validate state 값 업데이트

        instance.redraw(); // 재출력
    }, 300); // 입력한대로 값이 화면에 안보이면 유저가 당황할 수 있기 때문에, 0.3s 후에 값 업데이트. 10분 단위로만 입력 가능하다는 것을 Form 주석에 추가해둠
};

const destroyDatePicker = () => {
    emits('destroyDatePicker');
};

onMounted(() => {
    getBizPointPolicies();
})
</script>

<style lang="scss" scoped>
.form-input-group {
    display: flex;

    & + & {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebebeb;
    }

    .title {
        position: relative;
        padding-top: 7px;
        width: 25%;
        font-weight: 600;
        word-break: keep-all;

        &.required,
        .required {
            &::after {
                margin-left: 1px;
                content: '*';
                color: $danger;
                line-height: 100%;
                font-size: 10px;
                vertical-align: super;
            }
        }

        .caption {
            margin-top: 3px;
            color: $gray;
            font-size: 8px;
            font-weight: 500;
            line-height: 16px;
        }
    }

    .contents {
        width: 75%;

        .type-reserve-row {
            // 지급형태 > 예약지급 버튼 행
            display: flex;
            align-items: baseline;
            margin-top: 8px;
        }
    }

    .notice {
        margin-top: 10px;
        padding: 16px;
        border-radius: 8px;
        background-color: #f8f8fa;

        > p {
            margin-bottom: 5px;
            font-weight: bold;
        }
    }
}

.file-input-row {
    display: flex;
    flex-direction: column;

    input[type='file'] {
        display: none;

        &:focus {
            outline: 0;
        }
    }

    .file-path-box {
        width: 100%;
        height: 38px;
        padding: 5px 12px;
        border: 1px solid $black_16;
        border-radius: 6px;
        font-size: 14px;
        line-height: 1.8;
        overflow-x: auto;
        white-space: nowrap;
        cursor: pointer;

        &::-webkit-scrollbar {
            height: 3px;
        }

        &::-webkit-scrollbar-track {
            background: $black_08;
        }

        &::-webkit-scrollbar-thumb {
            &:horizontal {
                background: $black_40;
                border-radius: 6px;
            }
        }

        &::-webkit-scrollbar:vertical {
            display: none;
        }

        &--invalid {
            border-color: $danger;
        }

        &--empty {
            color: $black_32;
        }

        &:hover {
            border-color: $black_40;
        }
    }
}

.btn-row {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

:deep() {
    .n-button {
        &.upload-btn {
            margin-top: 8px;
            width: auto;

            .n-button__icon {
                margin: 0 0 0 16px !important;
                display: flex;
                justify-content: center;
                align-items: center;
                max-width: none;
                width: 40px;
                height: 100%;
                border-left: 1px solid rgba(255, 255, 255, 0.2);

                .n-icon-slot {
                    padding-left: 16px;
                    width: 100%;
                }
            }
        }
    }
}
</style>
